type RuntimeEnv = {
  ASSET_VERSION: string;
  API_URL: string;
  REACT_APP_API_URL: string;
  VITE_API_BASE_URL: string;
  HAAT_CAMPAIGNS_API_URL: string;
  GOOGLE_CLIENT_ID: string;
  FIREBASE_API_KEY: string;
  FIREBASE_AUTH_DOMAIN: string;
  FIREBASE_PROJECT_ID: string;
  FIREBASE_STORAGE_BUCKET: string;
  FIREBASE_MESSAGING_SENDER_ID: string;
  FIREBASE_APP_ID: string;
  FIREBASE_MEASUREMENT_ID: string;
  FIREBASE_VAPID_KEY: string;
  CAREERS_AI_CV_SCREENING_ENABLED: string;
  CARTO_BASEMAP_KEY: string;
  PARTNER_QUICKSIGHT_DASHBOARD_URL: string;
};

// Names accepted in window.__ENV__ that map onto a differently named RuntimeEnv
// key, so ops can set either the plain or the REACT_APP_-prefixed variable.
type RuntimeEnvAliases = {
  REACT_APP_CAREERS_AI_CV_SCREENING_ENABLED: string;
  REACT_APP_CARTO_BASEMAP_KEY: string;
  REACT_APP_PARTNER_QUICKSIGHT_DASHBOARD_URL: string;
};

declare global {
  interface Window {
    __ENV__?: Partial<RuntimeEnv & RuntimeEnvAliases>;
  }
}

// Treat empty values and unresolved envsubst tokens (e.g. "${API_URL}" or "__API_URL__")
// as missing so the build-time fallback kicks in during local development.
const isPlaceholder = (value?: string): boolean => {
  if (!value) return true;
  if (/^\$\{[A-Z0-9_]+\}$/.test(value)) return true;
  if (/^__[A-Z0-9_]+__$/.test(value)) return true;
  return false;
};

const pickFirst = (...values: Array<string | undefined>): string =>
  values.find((value) => !isPlaceholder(value)) ?? '';

const runtime =
  typeof window !== 'undefined' && window.__ENV__ ? window.__ENV__ : {};

export const env: RuntimeEnv = {
  ASSET_VERSION: pickFirst(runtime.ASSET_VERSION, 'dev'),
  API_URL: pickFirst(
    runtime.API_URL,
    runtime.REACT_APP_API_URL,
    process.env.REACT_APP_API_URL,
  ),
  REACT_APP_API_URL: pickFirst(
    runtime.REACT_APP_API_URL,
    runtime.API_URL,
    process.env.REACT_APP_API_URL,
  ),
  VITE_API_BASE_URL: pickFirst(
    runtime.VITE_API_BASE_URL,
    process.env.VITE_API_BASE_URL,
  ),
  HAAT_CAMPAIGNS_API_URL: pickFirst(
    runtime.HAAT_CAMPAIGNS_API_URL,
    process.env.REACT_APP_HAAT_CAMPAIGNS_API_URL,
  ),
  GOOGLE_CLIENT_ID: pickFirst(
    runtime.GOOGLE_CLIENT_ID,
    process.env.REACT_APP_GOOGLE_CLIENT_ID,
  ),
  FIREBASE_API_KEY: pickFirst(
    runtime.FIREBASE_API_KEY,
    process.env.REACT_APP_FIREBASE_API_KEY,
  ),
  FIREBASE_AUTH_DOMAIN: pickFirst(
    runtime.FIREBASE_AUTH_DOMAIN,
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  ),
  FIREBASE_PROJECT_ID: pickFirst(
    runtime.FIREBASE_PROJECT_ID,
    process.env.REACT_APP_FIREBASE_PROJECT_ID,
  ),
  FIREBASE_STORAGE_BUCKET: pickFirst(
    runtime.FIREBASE_STORAGE_BUCKET,
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  ),
  FIREBASE_MESSAGING_SENDER_ID: pickFirst(
    runtime.FIREBASE_MESSAGING_SENDER_ID,
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  ),
  FIREBASE_APP_ID: pickFirst(
    runtime.FIREBASE_APP_ID,
    process.env.REACT_APP_FIREBASE_APP_ID,
  ),
  FIREBASE_MEASUREMENT_ID: pickFirst(
    runtime.FIREBASE_MEASUREMENT_ID,
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  ),
  FIREBASE_VAPID_KEY: pickFirst(
    runtime.FIREBASE_VAPID_KEY,
    process.env.REACT_APP_FIREBASE_VAPID_KEY,
  ),
  CAREERS_AI_CV_SCREENING_ENABLED: pickFirst(
    runtime.CAREERS_AI_CV_SCREENING_ENABLED,
    runtime.REACT_APP_CAREERS_AI_CV_SCREENING_ENABLED,
    process.env.REACT_APP_CAREERS_AI_CV_SCREENING_ENABLED,
  ),
  CARTO_BASEMAP_KEY: pickFirst(
    runtime.CARTO_BASEMAP_KEY,
    runtime.REACT_APP_CARTO_BASEMAP_KEY,
    process.env.REACT_APP_CARTO_BASEMAP_KEY,
  ),
  PARTNER_QUICKSIGHT_DASHBOARD_URL: pickFirst(
    runtime.PARTNER_QUICKSIGHT_DASHBOARD_URL,
    runtime.REACT_APP_PARTNER_QUICKSIGHT_DASHBOARD_URL,
    process.env.REACT_APP_PARTNER_QUICKSIGHT_DASHBOARD_URL,
  ),
};
